import React from 'react';
import { useLocalStore, useObserver } from 'mobx-react';
import { ScoreDetailService } from '../../../../../../common/services';
import { runInAction } from 'mobx';
import BaseballMatchRelativeRecordModel from './model/record';
import BaseballMatchPlayerStatModel from './model/playerStat';

import {
  _STARTER_FITCHER_CHART_INIT,
  _COMPARISON_CHART_INIT,
  _SEPCIAL_CHART_INIT
} from './constants';
import BaseballMatchTeamStatModel from './model/teamStat';
import BaseballMatchRankModel from './model/rank';
import { ObjectUtil } from '../../../../../../common/utils/objectUtil';
import { ScoreUtil } from '../../../../../../common/utils/scoreUtil';

const BaseballMatchRelativeRecordStore = () => {
  const store = useLocalStore(() => ({
    playerStat: {
      home: {},
      away: {}
    },
    record: {
      recentAwayRecord: [],
      recentHomeRecord: [],
      vsRecord: [],
      selectedRecentRecordTab: 'home'
    },
    teamStat: {
      home: {},
      away: {}
    },
    rank: [],
    loading: true,
    init: false,
    gameId: null,
    get starterFitcherChart() {
      const { playerStat } = store;
      const { home, away } = playerStat;

      let result = ObjectUtil.deepClone(_STARTER_FITCHER_CHART_INIT);

      if (!!home.chart && !!away.chart) {
        result = {
          era: {
            home: home.chart.era,
            away: away.chart.era,
            visible: false
          },
          whip: {
            home: home.chart.whip,
            away: away.chart.whip,
            visible: false
          },
          hitAvg: {
            home: home.chart.hitAvg,
            away: away.chart.hitAvg,
            visible: false
          },
          so9: {
            home: home.chart.so9,
            away: away.chart.so9,
            visible: false
          },
          bb: {
            home: home.chart.bb,
            away: away.chart.bb,
            visible: false,
            isNpb: home.chart.bb.isNpb && away.chart.bb.isNpb
          }
        };

        Object.keys(result).forEach((key) => {
          if (result[key]['home'].p > 0 && result[key]['away'].p > 0) {
            const homeP = result[key]['home'].p;
            const awayP = result[key]['away'].p;

            result[key]['home']['active'] = false;
            result[key]['away']['active'] = false;
            result[key]['visible'] = true;

            if (key === 'so9') {
              if (homeP > awayP) {
                result[key]['home']['active'] = true;
              } else {
                result[key]['away']['active'] = true;
              }
            } else {
              if (homeP > awayP) {
                result[key]['away']['active'] = true;
              } else {
                result[key]['home']['active'] = true;
              }
            }

            if (homeP === awayP) {
              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
            }

            if (result[key]['home'].v === result[key]['away'].v) {
              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
            }

            const vHomeString = result[key]['home'].v + '';
            const vHomeLength = vHomeString.length * 2.25;
            const vAwayString = result[key]['away'].v + '';
            const vAwayLength = vAwayString.length * 2.25;
            let calc = 0;

            if (vHomeLength > homeP) {
              calc = vHomeLength - homeP + 3;
            }

            if (vAwayLength > awayP) {
              calc = vAwayLength - awayP + 3;
            }

            if (calc > 0) {
              result[key]['home'].p = homeP + calc;
              result[key]['away'].p = awayP + calc;
            }
          }
        });
      }

      return result;
    },
    get vsRecordSummary() {
      const { vsRecord } = store.record;
      const { playerStat } = store;
      const vsCount = vsRecord
        .slice()
        .map((item) => ScoreUtil.getSummaryScore(item, playerStat.home.id))
        .reduce(
          (a, c) => {
            if (c.home.score > c.away.score) {
              a.win++;
            } else if (c.away.score > c.home.score) {
              a.loss++;
            }

            return a;
          },
          { win: 0, loss: 0 }
        );
      const typeCount = vsRecord
        .slice()
        .map((item) =>
          ScoreUtil.getOddsTypeWinnerCount(item, playerStat.home.id)
        )
        .reduce(
          (a, c) => {
            if (c.handicap.win.length > 0) {
              a.h.w++;
            }
            if (c.handicap.loss.length > 0) {
              a.h.l++;
            }
            if (c.unover.win.length > 0) {
              a.u.w++;
            }
            if (c.unover.loss.length > 0) {
              a.u.l++;
            }

            return a;
          },
          { h: { w: 0, l: 0 }, u: { w: 0, l: 0 } }
        );

      let result = {
        vs: '',
        handicap: { text: '', red: '' },
        unover: { text: '', red: '' },
        home: { visible: false, text: '', bold: '' },
        away: { visible: false, text: '', bold: '' },
        win: { visible: false, text: '' }
      };

      if (vsRecord.length > 0) {
        result.vs = `홈팀(${store.teamStat.home.name})이 ${vsCount.win}승 ${vsCount.loss}패`;
        result.handicap = {
          text: `홈팀(${store.teamStat.home.name}) 기준 ${vsRecord.length}경기 중 `,
          red: `핸디 승 ${typeCount.h.w}회, 핸디 패 ${typeCount.h.l}회`
        };
        result.unover = {
          text: `홈팀(${store.teamStat.home.name}) 기준 ${vsRecord.length}경기 중 `,
          red: `언더 ${typeCount.u.w}회 오버 ${typeCount.u.l}회`
        };

        if (!!playerStat.home.starterPitcingStat) {
          result = getStarterHomeAway(
            playerStat,
            vsRecord.slice(),
            'home',
            result
          );
        }

        if (!!playerStat.away.starterPitcingStat) {
          result = getStarterHomeAway(
            playerStat,
            vsRecord.slice(),
            'away',
            result
          );
        }

        function getStarterHomeAway(stat, record, location, result) {
          const starterCount = record.reduce(
            (a, c) => {
              const playerId = stat[location].starterPitcingStat.player.id;
              let checkPlayer = false;
              let checkLocation = 'home';

              if (
                !!c.home.startPitcher &&
                playerId === c.home.startPitcher.id
              ) {
                checkPlayer = true;
                checkLocation = 'home';
              }

              if (
                !!c.away.startPitcher &&
                playerId === c.away.startPitcher.id
              ) {
                checkPlayer = true;
                checkLocation = 'away';
              }

              if (checkPlayer) {
                a.count++;

                if (c.odds.domesticWinLoseOdds.length > 0) {
                  const winType = checkLocation === 'home' ? 'WIN' : 'LOSS';
                  const awayType = checkLocation === 'home' ? 'LOSS' : 'WIN';

                  a.win =
                    a.win +
                    Number(
                      c.odds.domesticWinLoseOdds.filter(
                        (item) => item.type === winType && item.result === 'HIT'
                      ).length > 0
                    );

                  a.loss =
                    a.loss +
                    Number(
                      c.odds.domesticWinLoseOdds.filter(
                        (item) =>
                          item.type === awayType && item.result === 'HIT'
                      ).length > 0
                    );
                }

                if (c.odds.domesticUnderOverOdds.length > 0) {
                  const underType = checkLocation === 'home' ? 'UNDER' : 'OVER';
                  const overType = checkLocation === 'home' ? 'OVER' : 'UNDER';

                  a.under =
                    a.under +
                    Number(
                      c.odds.domesticUnderOverOdds.filter(
                        (item) =>
                          item.type === underType && item.result === 'HIT'
                      ).length > 0
                    );

                  a.over =
                    a.over +
                    Number(
                      c.odds.domesticUnderOverOdds.filter(
                        (item) =>
                          item.type === overType && item.result === 'HIT'
                      ).length > 0
                    );
                }
              }

              return a;
            },
            { count: 0, win: 0, loss: 0, under: 0, over: 0 }
          );

          if (starterCount.count > 0) {
            result[location] = {
              text: `${location === 'home' ? '홈팀' : '원정팀'}(${
                stat[location].name
              })은 `,
              bold: `${
                stat[location].starterPitcingStat.player.displayName
              } 선발(${starterCount.count})회 등판 시 ${starterCount.win}승 ${
                starterCount.loss
              }패, ${
                starterCount.under > 0 ? `언더 ${starterCount.under}회` : ''
              } ${starterCount.over > 0 ? `오버 ${starterCount.over}회` : ''}`,
              visible: true
            };
          }

          return result;
        }

        const winningCountArr = vsRecord
          .slice()
          .map((item) => ScoreUtil.getSummaryScore(item, playerStat.home.id));
        let winningCount = 0;
        let lossCount = 0;
        let countText = '';

        winningCountArr.some((item) => {
          if (item.home.score > item.away.score) {
            winningCount++;
          }

          return item.away.score > item.home.score;
        });

        winningCountArr.some((item) => {
          if (item.away.score > item.home.score) {
            lossCount++;
          }

          return item.home.score > item.away.score;
        });

        if (winningCount > 0) {
          countText = `${winningCount}연승 중입니다.`;
        } else {
          countText = `${lossCount}연패 중입니다.`;
        }

        result.win = {
          text: `최근 홈팀(${store.teamStat.home.name})이 원정팀(${store.teamStat.away.name})을 상대로 ${countText}`,
          visible: true
        };
      }

      return result;
    },
    get recentRecordSummary() {
      const {
        recentAwayRecord,
        recentHomeRecord,
        selectedRecentRecordTab
      } = store.record;
      const { playerStat } = store;
      let record = recentHomeRecord;

      let result = {
        total: { text: '', red: '' },
        type: { text: '', red: '' },
        win: { text: '', bold: '' }
      };

      if (selectedRecentRecordTab === 'away') {
        record = recentAwayRecord;
      }

      const count = record
        .slice()
        .map((item) =>
          ScoreUtil.getSummaryScore(
            item,
            playerStat[selectedRecentRecordTab].id
          )
        )
        .reduce(
          (a, c) => {
            if (c.home.score > c.away.score) {
              a.win++;
            } else if (c.away.score > c.home.score) {
              a.loss++;
            }

            return a;
          },
          { win: 0, loss: 0 }
        );

      const typeCount = record
        .slice()
        .map((item) =>
          ScoreUtil.getOddsTypeWinnerCount(
            item,
            playerStat[selectedRecentRecordTab].id
          )
        )
        .reduce(
          (a, c) => {
            if (c.handicap.win.length > 0) {
              a.h.w++;
            }
            if (c.handicap.loss.length > 0) {
              a.h.l++;
            }
            if (c.unover.win.length > 0) {
              a.u.w++;
            }
            if (c.unover.loss.length > 0) {
              a.u.l++;
            }

            return a;
          },
          { h: { w: 0, l: 0 }, u: { w: 0, l: 0 } }
        );

      result.total = {
        text: `${selectedRecentRecordTab === 'home' ? '홈' : '원정'}팀(${
          playerStat[selectedRecentRecordTab].name
        })은 `,
        red: `최근 ${record.length}경기 ${count.win}승 ${count.loss}패`
      };
      result.type = {
        text: `${record.length}경기 중 `,
        red: `핸디 승 ${typeCount.h.w}회/핸디 패 ${typeCount.h.l}회, 언더 ${typeCount.u.w}회/오버 ${typeCount.u.l}회`
      };

      const winningCountArr = record
        .slice()
        .map((item) =>
          ScoreUtil.getSummaryScore(
            item,
            playerStat[selectedRecentRecordTab].id
          )
        );
      let winningCount = 0;
      let lossCount = 0;
      let countText = '';

      winningCountArr.some((item) => {
        if (item.home.score > item.away.score) {
          winningCount++;
        }

        return item.away.score > item.home.score;
      });

      winningCountArr.some((item) => {
        if (item.away.score > item.home.score) {
          lossCount++;
        }

        return item.home.score > item.away.score;
      });

      if (winningCount > 0) {
        countText = `${winningCount}연승 중입니다.`;
      } else {
        countText = `${lossCount}연패 중입니다.`;
      }

      result.win = {
        text: `${selectedRecentRecordTab === 'home' ? '홈' : '원정'}팀(${
          playerStat[selectedRecentRecordTab].name
        })은 최근 ${countText}`,
        visible: true
      };
      // result.type

      return result;
    },
    get comparisonChart() {
      const { teamStat } = store;
      const { home, away } = teamStat;

      let result = ObjectUtil.deepClone(_COMPARISON_CHART_INIT);

      if (!!home.chart && !!away.chart) {
        Object.keys(_COMPARISON_CHART_INIT).forEach((key) => {
          if (key === 'hr' || key === 'sb' || key === 'err') {
            const max = Math.max(home.chart[key], away.chart[key]);
            let chartBase = 0;

            if (max >= 100) {
              chartBase = max + 100;
            } else if (max < 100) {
              chartBase = 100;
            } else if (max < 50) {
              chartBase = 50;
            }

            const homeP = +((home.chart[key] / chartBase) * 100).toFixed(1);
            const awayP = +((away.chart[key] / chartBase) * 100).toFixed(1);
            let homeActive = false;
            let awayActive = false;
            let visible = false;

            if (homeP > 0 && awayP > 0) {
              visible = true;

              if (key === 'hr' || key === 'sb') {
                if (home.chart[key] > away.chart[key]) {
                  homeActive = true;
                } else {
                  awayActive = true;
                }
              } else {
                if (home.chart[key] < away.chart[key]) {
                  homeActive = true;
                } else {
                  awayActive = true;
                }
              }

              if (home.chart[key] === away.chart[key]) {
                homeActive = false;
                awayActive = false;
              }
            }

            const data = {
              home: {
                p: homeP > 100 ? 100 : homeP,
                v: home.chart[key],
                active: homeActive
              },
              away: {
                p: awayP > 100 ? 100 : awayP,
                v: away.chart[key],
                active: awayActive
              },
              visible: visible
            };

            result[key] = data;
          } else {
            result[key] = {
              home: home.chart[key],
              away: away.chart[key],
              visible: false
            };

            if (result[key]['home'].p > 0 && result[key]['away'].p > 0) {
              result[key]['visible'] = true;

              const homeP = result[key]['home'].p;
              const awayP = result[key]['away'].p;

              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
              result[key]['visible'] = true;

              if (key === 'ops' || key === 'avg') {
                if (homeP > awayP) {
                  result[key]['home']['active'] = true;
                } else {
                  result[key]['away']['active'] = true;
                }
              } else {
                if (homeP > awayP) {
                  result[key]['away']['active'] = true;
                } else {
                  result[key]['home']['active'] = true;
                }
              }

              if (homeP === awayP) {
                result[key]['home']['active'] = false;
                result[key]['away']['active'] = false;
              }

              if (result[key]['home'].v === result[key]['away'].v) {
                result[key]['home']['active'] = false;
                result[key]['away']['active'] = false;
              }
            }
          }
        });
      }

      return result;
    },
    get specialChart() {
      const { teamStat } = store;
      const { home, away } = teamStat;

      let result = ObjectUtil.deepClone(_SEPCIAL_CHART_INIT);

      if (!!home.specialChart && !!away.specialChart) {
        Object.keys(_SEPCIAL_CHART_INIT).forEach((key) => {
          result[key] = {
            home: home.specialChart[key],
            away: away.specialChart[key],
            visible:
              home.specialChart[key].p > 0 && away.specialChart[key].p > 0
          };

          result[key]['home']['active'] = false;
          result[key]['away']['active'] = false;

          if (home.specialChart[key].p > away.specialChart[key].p) {
            result[key]['home']['active'] = true;
          } else {
            result[key]['away']['active'] = true;
          }

          if (home.specialChart[key].p === away.specialChart[key].p) {
            result[key]['home']['active'] = false;
            result[key]['away']['active'] = false;
          }
        });
      }

      return result;
    },
    async getPlayerStat(gameId) {
      const result = await ScoreDetailService.getPlayerStatBySportsType(
        'baseball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    async getRecord(gameId) {
      const result = await ScoreDetailService.getRecordBySportsType(
        'baseball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    async getRank(gameId) {
      const result = await ScoreDetailService.getRankBySportsType(
        'baseball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    async getTeamStat(gameId) {
      const result = await ScoreDetailService.getTeamStatBySportsType(
        'baseball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    getAll: async (gameId) => {
      store.loading = true;

      const [playerStat, record, teamStat, rank] = await Promise.all([
        store.getPlayerStat(gameId),
        store.getRecord(gameId),
        store.getTeamStat(gameId),
        store.getRank(gameId)
      ]);

      runInAction(() => {
        store.playerStat = {
          home: new BaseballMatchPlayerStatModel(
            playerStat.home,
            playerStat.away.name
          ),
          away: new BaseballMatchPlayerStatModel(
            playerStat.away,
            playerStat.home.name
          )
        };
        store.record = new BaseballMatchRelativeRecordModel(
          record,
          teamStat.home,
          teamStat.away
        );
        store.teamStat = {
          home: new BaseballMatchTeamStatModel(teamStat.home, 'home'),
          away: new BaseballMatchTeamStatModel(teamStat.away, 'away')
        };
        store.rank = new BaseballMatchRankModel(
          rank,
          teamStat.home.id,
          teamStat.away.id
        );
        store.loading = false;
      });
    }
  }));

  return {
    getAll: store.getAll,
    data: useObserver(() => {
      return {
        init: store.init,
        loading: store.loading,
        playerStat: store.playerStat,
        record: store.record,
        starterFitcherChart: store.starterFitcherChart,
        vsRecordSummary: store.vsRecordSummary,
        recentRecordSummary: store.recentRecordSummary,
        teamStat: store.teamStat,
        comparisonChart: store.comparisonChart,
        specialChart: store.specialChart,
        rank: store.rank
      };
    })
  };
};

export default BaseballMatchRelativeRecordStore;
